import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Card from '@/components/ui/Card'
import LeaderboardCard from '@/components/leaderboard/LeaderboardCard'
import { useAuthContext } from '@/lib/auth-context'
import { useProspectLockerRoom } from '@/hooks/useProspectLockerRoom'
import {
  ATHLETIC_METRICS,
  HIGH_SCHOOL_SPORTS,
  SPORT_METRICS,
  getClassYears,
  getMetricPercentile,
  getMetricsForSport,
  getPositionsForSport,
  formatMetricValue,
  type SportKey,
  type LeaderboardScope,
  type LeaderboardRanking,
  type ProspectLeaderboardProfile,
  type MetricDefinition,
} from '@/lib/leaderboard-types'
import { supabase } from '@/supabase/client'

// US States for filtering
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

type LeaderboardState = {
  loading: boolean
  error: string
  rankings: LeaderboardRanking[]
  total: number
}

const INITIAL_STATE: LeaderboardState = {
  loading: true,
  error: '',
  rankings: [],
  total: 0,
}

export default function Leaderboard() {
  const { profile } = useAuthContext() as any
  const { lockerRoom, prospectId } = useProspectLockerRoom()
  const isCoach = profile?.role === 'coach'

  // Filters
  const [sport, setSport] = useState<SportKey>('football')
  const [position, setPosition] = useState<string>('')
  const [classYear, setClassYear] = useState<number | null>(null)
  const [state, setState] = useState<string>('')
  const [scope, setScope] = useState<LeaderboardScope>('platform')
  const [metricId, setMetricId] = useState<string>('forty_yard')

  // Data
  const [leaderboardState, setLeaderboardState] = useState<LeaderboardState>(INITIAL_STATE)

  // Available metrics for selected sport
  const availableMetrics = useMemo(() => getMetricsForSport(sport), [sport])
  const positions = useMemo(() => getPositionsForSport(sport), [sport])
  const classYears = useMemo(() => getClassYears(), [])
  const currentMetric = ATHLETIC_METRICS[metricId]

  // Reset metric if not available for sport
  useEffect(() => {
    const sportMetrics = SPORT_METRICS[sport] || []
    if (!sportMetrics.includes(metricId)) {
      setMetricId(sportMetrics[0] || 'vertical_jump')
    }
  }, [sport, metricId])

  // Reset position when sport changes
  useEffect(() => {
    setPosition('')
  }, [sport])

  // Load leaderboard data
  const loadLeaderboard = useCallback(async () => {
    setLeaderboardState((prev) => ({ ...prev, loading: true, error: '' }))

    try {
      // Build query for prospect_metrics table
      // Note: Supabase doesn't support filtering on joined tables directly,
      // so we fetch all metrics for this metric_id and filter client-side
      const { data, error } = await supabase
        .from('prospect_metrics')
        .select(`
          id,
          prospect_id,
          metric_id,
          value,
          recorded_at,
          verified,
          source,
          prospect:prospects(
            id,
            name,
            avatar_url,
            headline,
            bio
          )
        `)
        .eq('metric_id', metricId)
        .limit(500) // Fetch more to allow for filtering

      if (error) {
        throw error
      }

      // Filter client-side based on prospect attributes
      let filtered = (data || []).filter((row: any) => {
        if (!row.prospect) return false

        // Filter by sport (column not yet in DB — skip for now)
        // if (sport && row.prospect.sport !== sport) return false

        // Filter by position if selected (column not yet in DB — skip for now)
        // if (position && row.prospect.position !== position) return false

        // Filter by class year if selected (column not yet in DB — skip for now)
        // if (classYear && row.prospect.class_year !== classYear) return false

        // Filter by state if selected (column not yet in DB — skip for now)
        // if (state && row.prospect.state !== state) return false

        return true
      })

      // Sort by value (direction depends on metric)
      const metric = ATHLETIC_METRICS[metricId]
      if (metric?.direction === 'lower_better') {
        filtered.sort((a: any, b: any) => a.value - b.value)
      } else {
        filtered.sort((a: any, b: any) => b.value - a.value)
      }

      // Limit to top 100
      filtered = filtered.slice(0, 100)

      // Transform to rankings
      const rankings: LeaderboardRanking[] = filtered
        .map((row: any, idx: number) => ({
          rank: idx + 1,
          prospect: {
            prospect_id: row.prospect_id,
            name: row.prospect.name,
            avatar_url: row.prospect.avatar_url,
            sport: row.prospect?.sport || null,
            position: row.prospect?.position || null,
            class_year: row.prospect?.class_year || null,
            city: row.prospect?.city || null,
            state: row.prospect?.state || null,
            school: row.prospect?.school || null,
            metrics: {},
          } as ProspectLeaderboardProfile,
          value: row.value,
          percentile: getMetricPercentile(metricId, row.value),
        }))

      setLeaderboardState({
        loading: false,
        error: '',
        rankings,
        total: rankings.length,
      })
    } catch (err: any) {
      console.error('[Leaderboard] load error', err)
      setLeaderboardState({
        loading: false,
        error: err?.message || 'Unable to load leaderboard',
        rankings: [],
        total: 0,
      })
    }
  }, [metricId, sport, position, classYear, state, scope, lockerRoom?.id])

  useEffect(() => {
    loadLeaderboard()
  }, [loadLeaderboard])

  // Find current user's rank
  const myRank = useMemo(() => {
    if (!prospectId) return null
    return leaderboardState.rankings.find((r) => r.prospect.prospect_id === prospectId)
  }, [leaderboardState.rankings, prospectId])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900">Athletic Leaderboards</h2>
          <p className="mt-1 text-sm text-stone-600">
            Compare your metrics against other high school athletes. Track your progress and see where you rank.
          </p>
        </div>
        {myRank && currentMetric && (
          <Card className="bg-gradient-to-r from-cocoa to-cocoa/80 text-white">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-white/70">Your {currentMetric.shortLabel}</p>
                <p className="text-2xl font-bold">{formatMetricValue(metricId, myRank.value)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/70">Rank</p>
                <p className="text-2xl font-bold">#{myRank.rank}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-white/70">Percentile</p>
                <p className="text-lg font-semibold">{myRank.percentile.toFixed(0)}%</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Filters */}
      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-stone-900">Filters</h3>
          <button
            type="button"
            onClick={() => {
              setPosition('')
              setClassYear(null)
              setState('')
              setScope('platform')
            }}
            className="text-sm text-stone-500 hover:text-stone-700"
          >
            Reset filters
          </button>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {/* Sport */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">Sport</label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value as SportKey)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              {Object.entries(HIGH_SCHOOL_SPORTS).map(([key, val]) => (
                <option key={key} value={key}>
                  {val.label}
                </option>
              ))}
            </select>
          </div>

          {/* Position */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">Position</label>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              <option value="">All Positions</option>
              {positions.map((pos) => (
                <option key={pos.value} value={pos.value}>
                  {pos.label}
                </option>
              ))}
            </select>
          </div>

          {/* Class Year */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">Class</label>
            <select
              value={classYear ?? ''}
              onChange={(e) => setClassYear(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              <option value="">All Classes</option>
              {classYears.map((yr) => (
                <option key={yr.value} value={yr.value}>
                  {yr.label}
                </option>
              ))}
            </select>
          </div>

          {/* State */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">State</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              <option value="">All States</option>
              {US_STATES.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>

          {/* Scope */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">Scope</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as LeaderboardScope)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              <option value="platform">All Platform</option>
              <option value="locker_room" disabled={!lockerRoom?.id}>
                My Locker Room
              </option>
              <option value="state" disabled={!state}>
                State Only
              </option>
            </select>
          </div>

          {/* Metric */}
          <div>
            <label className="mb-1 block text-xs font-medium text-stone-600">Metric</label>
            <select
              value={metricId}
              onChange={(e) => setMetricId(e.target.value)}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm font-medium focus:border-cocoa focus:outline-none focus:ring-1 focus:ring-cocoa"
            >
              {availableMetrics.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Metric Quick Select */}
      <div className="flex flex-wrap gap-2">
        {availableMetrics.slice(0, 8).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMetricId(m.id)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              metricId === m.id
                ? 'bg-cocoa text-white shadow'
                : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
            }`}
          >
            {m.shortLabel}
          </button>
        ))}
        {availableMetrics.length > 8 && (
          <span className="flex items-center px-2 text-xs text-stone-500">
            +{availableMetrics.length - 8} more in dropdown
          </span>
        )}
      </div>

      {/* Leaderboard */}
      {leaderboardState.error ? (
        <Card className="border-brick/30 bg-brick/10 text-brick">
          <p>{leaderboardState.error}</p>
        </Card>
      ) : leaderboardState.loading ? (
        <Card className="animate-pulse space-y-4">
          <div className="h-6 w-48 rounded bg-stone-200" />
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-8 w-8 rounded-full bg-stone-200" />
              <div className="h-10 w-10 rounded-full bg-stone-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 rounded bg-stone-200" />
                <div className="h-3 w-48 rounded bg-stone-200" />
              </div>
              <div className="h-6 w-16 rounded bg-stone-200" />
            </div>
          ))}
        </Card>
      ) : currentMetric ? (
        <LeaderboardCard
          metric={currentMetric}
          rankings={leaderboardState.rankings}
          title={`${HIGH_SCHOOL_SPORTS[sport]?.label} - ${currentMetric.label}`}
          showMore={false}
          highlight={prospectId ?? undefined}
        />
      ) : null}

      {/* Stats Summary */}
      {!leaderboardState.loading && leaderboardState.rankings.length > 0 && currentMetric && (
        <Card className="space-y-4">
          <h3 className="font-semibold text-stone-900">Distribution Stats</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-stone-50 p-4">
              <p className="text-xs text-stone-500">Best</p>
              <p className="text-lg font-semibold text-stone-900">
                {formatMetricValue(metricId, leaderboardState.rankings[0]?.value ?? 0)}
              </p>
            </div>
            <div className="rounded-xl bg-stone-50 p-4">
              <p className="text-xs text-stone-500">Median</p>
              <p className="text-lg font-semibold text-stone-900">
                {formatMetricValue(
                  metricId,
                  leaderboardState.rankings[Math.floor(leaderboardState.rankings.length / 2)]?.value ?? 0
                )}
              </p>
            </div>
            <div className="rounded-xl bg-stone-50 p-4">
              <p className="text-xs text-stone-500">Total Athletes</p>
              <p className="text-lg font-semibold text-stone-900">{leaderboardState.total}</p>
            </div>
            <div className="rounded-xl bg-stone-50 p-4">
              <p className="text-xs text-stone-500">Elite Threshold</p>
              <p className="text-lg font-semibold text-emerald-700">
                {formatMetricValue(metricId, currentMetric.typical.elite)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!leaderboardState.loading && leaderboardState.rankings.length === 0 && (
        <Card className="py-12 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-stone-100 p-4">
            <svg
              className="h-8 w-8 text-stone-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h3 className="mt-4 font-semibold text-stone-900">No Rankings Yet</h3>
          <p className="mt-1 text-sm text-stone-500">
            Be the first to record your {currentMetric?.label || 'metrics'}!
          </p>
          <button
            type="button"
            className="mt-4 rounded-lg bg-cocoa px-4 py-2 text-sm font-semibold text-white shadow hover:bg-cocoa/90"
          >
            Log My Stats
          </button>
        </Card>
      )}

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h4 className="font-semibold text-blue-900">About Leaderboards</h4>
            <ul className="mt-1 space-y-1 text-sm text-blue-800">
              <li>Rankings are based on verified and self-reported metrics</li>
              <li>Verified times from camps and combines are marked with a badge</li>
              <li>Your coach can verify your metrics to boost credibility</li>
              <li>Metrics are sport-specific and compared against position averages</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>
  )
}
